import { image } from 'utils/image';
import { clear, transitionEnd } from 'utils/node';
import formatMoney from 'utils/format-money';
import { renderProductItem, Product as ProductType } from 'utils/js-templates';

type Attribute =
  'age' |
  'categories' |
  'created_at' |
  'description' |
  'handle' |
  'id' |
  'image' |
  'images' |
  'name' |
  'on_sale' |
  'price' |
  'price_min' |
  'price_max' |
  'list_price' |
  'published' |
  'sku' |
  'stock' |
  'type' |
  'url' |
  'variant_inventory_policy' |
  'variant_list_prices' |
  'variant_names' |
  'variant_prices' |
  'variants' |
  'variant_stocks' |
  'vendor' |
  'tags';

type Endpoint = {
  url: 'search/categories' | 'search/predictive' | 'search/search'
  type: string
  body: {
    query: string
    limit: number
    offset?: number
    labels?: string[]
    attributes?: Attribute[]
  }
};

type ClerkCategory = {
  children: any[]
  description: string
  id: number
  image: string
  name: string
  parent: null
  subcategories: any[]
  url: string
};

export type ClerkResponse = {
  custom_type: string
  hits?: number
  product_data?: any[]
  query?: string
  categories?: ClerkCategory[]
  result: number[]
  status: string
};

export type Config = {
  api_key: string
  header?: HTMLElement
  form?: HTMLFormElement
  categoriesList?: HTMLElement
  productsList?: HTMLElement
  showResultsButtons?: NodeListOf<HTMLElement>
  resultHitsCounters?: NodeListOf<HTMLElement>
  overlay?: HTMLElement
};

class ClerkManager {
  public readonly config:Config;

  private loadedProducts: number;

  private products: any[];

  constructor(config: Config) {
    this.config = { ...config };
    this.loadedProducts = 0;
    this.products = [];
  }

  search = async (endpoints:Endpoint[]) => {
    const fetches = endpoints.map(async (endpoint) => {
      const body = {
        key: this.config.api_key,
        ...endpoint.body,
      };
      const clerkCall = new Promise((resolve) => {
        window.Clerk('call', endpoint.url, body, (callResponse) => {
          const response = {
            custom_type: endpoint.type,
            ...callResponse,
          };
          resolve(response);
        });
      });
      return clerkCall;
    });
    const returnedResults = await Promise.all(fetches).then((data:ClerkResponse[]) => data);
    return returnedResults;
  };

  buildPredictiveHTML = (result:ClerkResponse) => {
    const regex = new RegExp(result.query, 'gi');

    clear(this.config.productsList);
    let html = '';
    if (result.product_data.length > 0) {
      result.product_data.forEach((product) => {
        const imageObject = {
          src: product.image,
          alt: '',
          height: 400,
          width: 400,
        };

        const title = product.name.replace(regex, '<mark>$&</mark>');

        html += `
          <div class="relative grid grid-cols-[82px_1fr]" data-clerk-product-id="${product.id}">
            <div class="aspect-ratio aspect-ratio--1/1 bg-neutral-200 rounded-xl">
              ${product.image && product.image !== '' ? image(imageObject) : null}
            </div>
            <div class="flex flex-col justify-center pl-5">
              <p class="font-bold text-black">${title}</p>
              <p class="font-bold text-panzer-400">${formatMoney(product.price * 100)}</p>
            </div>
            <a href="${product.url}" class="absolute top-0 left-0 w-full h-full z-10"></a>
          </div>
        `;
      });
    } else {
      html += `<p class="text-neutral-500">${window.theme.strings.search.noResults}</p>`;
    }
    this.config.productsList.insertAdjacentHTML('afterbegin', html);
    window.Clerk('click', '*[data-clerk-product-id]');

    if (result.hits > 6) {
      this.config.showResultsButtons.forEach((button) => {
        button.hidden = false;
      });

      this.config.resultHitsCounters.forEach((counter) => {
        counter.innerHTML = result.hits.toString();
      });
    } else {
      this.config.showResultsButtons.forEach((button) => {
        button.hidden = true;
      });
    }
  };

  buildCategoriesHTML = (result:ClerkResponse) => {
    clear(this.config.categoriesList);

    let html = '';
    if (result.categories.length > 0) {
      result.categories.forEach((category) => {
        html += `
          <a class="text-sm text-neutral-500 mb-2 last:mb-0 hover:underline" href="${category.url}">${category.name}</a>
        `;
      });
    } else {
      html += `<p class="text-neutral-500">${window.theme.strings.search.noResults}</p>`;
    }

    this.config.categoriesList.insertAdjacentHTML('afterbegin', html);
  };

  fetchShopifyProducts = async (products) => {
    const fetches = [];
    for (let i = 0; i < products.length; i += 1) {
      if (products[i].handle) {
        fetches.push(fetch(`products/${products[i].handle}?view=data`).then((r) => r.json()));
      }
    }
    const productsArray = await Promise.all(fetches).then((data) => data.flat());
    this.products = productsArray;
  };

  buildProductItem = async (product) => {
    const productItem:ProductType = {
      id: product.id,
      available: product.available,
      compareAtPrice: product.compare_at_price,
      price: product.price,
      tags: product.tags,
      title: product.title,
      type: product.type,
      url: product.url,
      variantID: product.variant_id,
      vendor: product.vendor,
      labels: product.labels,
      clerk: true,
    };

    if (product.images.length > 0) {
      productItem.image = {
        alt: product.images[0].alt,
        src: product.images[0].src,
        width: product.images[0].width,
        height: product.images[0].height,
      };
    }

    this.config.productsList.insertAdjacentHTML('beforeend', renderProductItem(productItem));
  };

  insertProductItems = async (products) => {
    await this.fetchShopifyProducts(products).then(() => {
      if (this.loadedProducts === 0) {
        clear(this.config.productsList);
      }
      this.products.forEach((product) => this.buildProductItem(product));
    });
    window.Clerk('click', '*[data-clerk-product-id]');
  };

  setLoadedProducts = (number) => {
    this.loadedProducts = number;
  };

  open = () => {
    this.config.header.setAttribute('data-search-open', 'true');
    this.config.overlay.setAttribute('data-active', 'true');
    document.dispatchEvent(new Event('search:opened'));
  };

  close = () => {
    this.config.header.classList.remove('rounded-b-none');
    this.config.header.setAttribute('data-search-open', 'false');
    this.config.overlay.setAttribute('data-active', 'false');
    // clear(this.config.categoriesList); // TODO: clerk-categories-fix
    clear(this.config.productsList);
    transitionEnd(this.config.form, () => {
      document.dispatchEvent(new Event('search:closed'));
    }, { once: true });
  };

  attachListeners = () => {
    window.addEventListener('keydown', (e:KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    });

    document.addEventListener('search:close', () => {
      this.close();
    });

    document.addEventListener('search:open', () => {
      this.open();
    });

    this.config.overlay.addEventListener('click', () => {
      this.close();
    });

    this.config.showResultsButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.config.form.submit();
      });
    });
  };
}

export default ClerkManager;
